import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import '../admin/AdminComponents.css';

const TextureForm = ({ texture, onClose }) => {
  const { textures, addTexture, updateTexture } = useAdmin();
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    color: '#000000'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (texture) {
      setFormData({
        id: texture.id || '',
        name: texture.name || '',
        color: texture.color || '#000000'
      });
    } else {
      setFormData({
        id: '',
        name: '',
        color: '#000000'
      });
    }
  }, [texture]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id.trim()) {
      newErrors.id = 'Texture ID is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.id)) {
      newErrors.id = 'Texture ID can only contain lowercase letters, numbers, and underscores';
    } else if (!texture && textures.find(t => t.id === formData.id)) {
      newErrors.id = 'Texture ID already exists';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.color || !/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = 'Color must be a valid hex color (e.g., #FF0000)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (texture) {
        await updateTexture(texture.id, {
          name: formData.name.trim(),
          color: formData.color.trim()
        });
      } else {
        await addTexture({
          id: formData.id.trim(),
          name: formData.name.trim(),
          color: formData.color.trim()
        });
      }
      onClose();
    } catch (error) {
      alert(error.message || 'Failed to save texture');
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3 className="admin-modal-title">
            {texture ? 'Edit Texture' : 'Add Texture'}
          </h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal-body">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label className="admin-form-label">ID *</label>
              <input
                type="text"
                name="id"
                className={`admin-form-input ${errors.id ? 'error' : ''}`}
                value={formData.id}
                onChange={handleChange}
                disabled={!!texture}
                required
                placeholder="e.g., black, white, red"
              />
              {errors.id && <span className="admin-error-message">{errors.id}</span>}
              {texture && (
                <small className="admin-form-hint">Texture ID cannot be changed</small>

              )}
              {!texture && (
                <small className="admin-form-hint">Use lowercase letters, numbers, and underscores only</small>
              )}
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Name *</label>
              <input
                type="text"
                name="name"
                className={`admin-form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Black, White, Red"
              />
              {errors.name && <span className="admin-error-message">{errors.name}</span>}
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Color *</label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input
                  type="color"
                  name="color"
                  className={`admin-form-input ${errors.color ? 'error' : ''}`}
                  value={formData.color}
                  onChange={handleChange}
                  required
                  style={{ width: '80px', height: '40px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                      handleChange({ target: { name: 'color', value } });
                    }
                  }}
                  className={`admin-form-input ${errors.color ? 'error' : ''}`}
                  placeholder="#000000"
                  style={{ flex: 1 }}
                  maxLength={7}
                />
              </div>

              {errors.color && <span className="admin-error-message">{errors.color}</span>}
              <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  style={{
                    width: '60px',
                    height: '30px',
                    backgroundColor: formData.color,
                    border: '2px solid var(--border)',
                    borderRadius: '4px'
                  }}
                />
                <small className="admin-form-hint">Preview</small>

              </div>

            </div>


            <div className="admin-form-actions">
              <button type="button" className="admin-button" onClick={onClose}>
                Cancel
              </button>

              <button type="submit" className="admin-button primary">
                {texture ? 'Update' : 'Create'}
              </button>

            </div>

          </form>

        </div>

      </div>

    </div>

  );
};

export default TextureForm;

